import { Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, Type } from '@angular/core';
import { VideoEntity, VideoLibraryEntity, VideoStreamingProviderAllItems, VideoStreamingProviderEntity } from '../../../../meta-data/app-meta-data.service';
import { VideoRepositoryService } from '../../services/video-repository.service';
import { VideoComponentBase } from '../../models/video-component.base.interface';
import { VideoPlayerBunnyComponent } from '../streaming-providers/bunny/components/video-player-bunny/video-player-bunny.component';
import { VideoEvent } from '../../models/video-event.model';
import { VideoPlayerApiVideoComponent } from '../streaming-providers/apivideo/components/video-player-apivideo/video-player-apivideo.component';
import { VideoEventType } from '../../models/video-event-type.enum';
import { VideoBufferingUtilService } from '../../services/video-buffering-util.service';
import {
  booleanConverter,
  DwInputConverter,
  DwLoggingService,
  DwMessage,
  DwMessageSeverity,
  isFirstChange
} from '@devwareapps/devware-cap';
import { environment } from '../../../../../environments/environment';
import { HelpScoutBeaconService } from '../../../help-scout-beacon/services/help-scout-beacon.service';
import { VideoPlayback } from '../../models/video-playback.model';
import { VideoPlayerBunnyHlsComponent } from '../streaming-providers/bunny/components/video-player-bunny-hls/video-player-bunny-hls.component';
import { BunnyPlayerTestSettings } from '../../models/bunny-player-test-settings.model';
import { BunnyPlayerTestSettingsService } from '../../services/bunny-player-test-settings.service';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';

@Component({
  selector: 'app-video-host',
  templateUrl: './video-host.component.html',
  styleUrls: ['./video-host.component.scss']
})
export class VideoHostComponent implements OnInit, OnChanges, OnDestroy, VideoComponentBase {
  @Input() video: VideoEntity;
  @Input() furthestWatchedTime: number = 0;
  @Input() playback: VideoPlayback;

  @DwInputConverter(booleanConverter)
  @Input() allowSeekOverride: boolean = false;
  videoStreamingProvider: VideoStreamingProviderEntity;
  videoLibrary: VideoLibraryEntity;

  @Output() videoEvent = new EventEmitter<VideoEvent>();
  @Output() videoWidthChanged = new EventEmitter<string>();

  @Output() initCompleted = new EventEmitter<boolean>();
  videoComponentInstance: VideoComponentBase;
  videoComponentType: Type<any>;
  videoEventSubscription: any;
  initComplete: boolean = false;
  initCompleteSubscription: any;
  interval: any;

  bunnyPlayerProviderId = VideoStreamingProviderAllItems.BunnyNet;
  apiVideoPlayerProviderId = VideoStreamingProviderAllItems.ApiVideo;
  useBunnyHlsPlayer = false;
  showBunnyHlsDiagnostics = false;
  selectedPlayback: VideoPlayback;
  effectiveAllowSeekOverride = false;
  preventSeekOverride = false;

  hidePlayer: boolean = false;

  timeToWaitForInit = 20000;

  initCount: number = 0;
  maxInitCount: number = 5;

  message: DwMessage;
  initTime: number;
  initTimeStart: any;

  destroyed: boolean = false;
  videoSetupComplete: boolean;
  private currentVideoSignature: string = null;
  private playerTestSettings: BunnyPlayerTestSettings;
  private playerTestSettingsSubscription: Subscription;
  private playerTestSettingsActive = false;

  constructor(private videoRepositoryService: VideoRepositoryService,
    private videoBufferingUtilService: VideoBufferingUtilService,
    private dwLoggingService: DwLoggingService,
    private helpScoutBeaconService: HelpScoutBeaconService,
    private bunnyPlayerTestSettingsService: BunnyPlayerTestSettingsService

  ) { }

  ngOnInit(): void {
    this.applyPlayerTestSettings(this.bunnyPlayerTestSettingsService.current);
    this.playerTestSettingsSubscription = this.bunnyPlayerTestSettingsService.settings$
      .pipe(skip(1))
      .subscribe(settings => {
        this.applyPlayerTestSettings(settings);
        this.refreshVideoIfNeeded(true);
      });

    this.refreshVideoIfNeeded();

    this.turnOffBeacon();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Initial input setup is handled by ngOnInit after the session player
    // override has been loaded. Only react to subsequent input changes here.
    if (isFirstChange(changes)) {
      return;
    }

    if (changes.video) {
      this.refreshVideoIfNeeded(true);
    } else if (changes.playback) {
      const previousFormat = changes.playback.previousValue?.OnlineSource?.Format?.toLowerCase();
      const currentFormat = changes.playback.currentValue?.OnlineSource?.Format?.toLowerCase();
      const previousUrl = changes.playback.previousValue?.OnlineSource?.Url;
      const currentUrl = changes.playback.currentValue?.OnlineSource?.Url;

      // A direct lesson URL can render the legacy iframe before its module
      // bundle arrives. Upgrade it once when the selected playback source
      // becomes available. A library mode change can also switch the active
      // player type. A URL change within the same format means the lesson,
      // video, or signed source changed and the active player must be rebuilt.
      if (previousUrl !== currentUrl && currentUrl) {
        this.refreshVideoIfNeeded(true);
      } else if ((!previousFormat || previousFormat !== currentFormat)
        && ['bunny-embed', 'hls'].includes(currentFormat)) {
        this.refreshVideoIfNeeded();
      }
    }
  }



  ngOnDestroy(): void {
    this.destroyed = true;

    if (this.videoEventSubscription) {
      this.videoEventSubscription.unsubscribe();
    }
    if (this.initCompleteSubscription) {
      this.initCompleteSubscription.unsubscribe();
    }

    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    this.playerTestSettingsSubscription?.unsubscribe();

    this.turnOnBeacon();
  }

  turnOnBeacon() {
    this.helpScoutBeaconService.turnOnBeacon();
  }

  turnOffBeacon() {
    if (environment.turnOffBeaconForVideos) {
      this.helpScoutBeaconService.turnOffBeacon();
    }
  }



  setupVideo(): void {
    this.initComplete = false;
    this.videoSetupComplete = false;
    this.hidePlayer = false;
    this.message = null;
    this.videoStreamingProvider = null;
    this.videoLibrary = null;
    this.selectedPlayback = this.playback;
    this.effectiveAllowSeekOverride = this.allowSeekOverride;
    this.preventSeekOverride = false;
    this.showBunnyHlsDiagnostics = false;

    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    this.initTime = null;
    this.initTimeStart = null;

    if (!this.video) {
      this.currentVideoSignature = null;
      return;
    }

    this.loadVideoDependencies();

    //this.setupPlayerHealthCheck();
  }

  private refreshVideoIfNeeded(forceRefresh: boolean = false) {
    const nextSignature = this.getVideoSignature();

    if (!forceRefresh && nextSignature === this.currentVideoSignature) {
      return;
    }

    this.currentVideoSignature = nextSignature;
    this.setupVideo();
  }

  private getVideoSignature(): string {
    if (!this.video) {
      return null;
    }

    const playbackSource = this.playback?.OnlineSource;
    const playbackFormat = playbackSource?.Format?.toLowerCase();

    return [
      this.video.VideoId,
      this.video.VideoKey,
      this.video.DirectPlayUrl,
      this.video.VideoLibraryId,
      this.video.VideoStreamingProviderId,
      playbackFormat === 'bunny-embed' || playbackFormat === 'hls'
        ? playbackSource?.Url
        : null,
      this.playback?.PlaybackConfigRevision
    ].join('|');
  }

  private setupPlayerHealthCheck() {

    if (this.destroyed) {
      return;
    }

    if (!this.video) {
      return;
    }

    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    // If init isn't complete in timeToWaitForInit milliseconds, we will toggle the player visibility to ensure it is rendered
    this.interval = setInterval(() => {
      //console.log('VideoHostComponent: Interval Check');
      if (this.destroyed) {
        return;
      }

      if (this.initComplete) {
        //console.warn('VideoHostComponent:Video player init, stopping health check interval');
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }

        return;
      }

      if (!this.initComplete) {
        console.warn('VideoHostComponent:Video player init not complete yet');

        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }

        this.initCount++;

        if (this.initCount > 2) {
          this.message = {
            severity: DwMessageSeverity.error,
            messageBody: 'Error initializing video player. Please try again later.',
            allowClose: false,
          }

          this.dwLoggingService.logVerbose(`Video player init not complete after ${this.initCount} failures`, null, true);

          return;
        }

        this.dwLoggingService.logVerbose(`Video player init not complete after ${this.timeToWaitForInit} milliseconds. Attempting to re-initialize. (Hiding player) Count: ${this.initCount}`, null, true);

        // we need to toggle the player visibility to ensure it is rendered
        this.hidePlayer = true;

        setTimeout(() => {
          this.hidePlayer = false;

          console.log('VideoHostComponent: Re-displaying video player');

          this.setupPlayerHealthCheck();
        }, 50);
      }

    }, this.timeToWaitForInit);
  }


  private loadVideoDependencies() {
    if (!this.video) {
      return;
    }


    this.videoRepositoryService.getStreamingProvider(this.video.VideoStreamingProviderId)
      .subscribe(videoStreamingProvider => {
        this.videoStreamingProvider = videoStreamingProvider;
        this.videoLibrary = this.videoRepositoryService.getVideoLibrary(videoStreamingProvider, this.video.VideoLibraryId);

        // Bunny playback selection depends on the module playback response.
        // Do not briefly render the legacy iframe while that response is still loading.
        if (videoStreamingProvider.VideoStreamingProviderId === VideoStreamingProviderAllItems.BunnyNet
          && !this.playback) {
          return;
        }

        this.determineVideoComponentType();

        // If the video component has already been initialized once, we can just update the inputs. If not, they will be set when the component initializes
        if (this.videoComponentInstance) {
          this.configureVideoComponent(this.videoComponentInstance);
        }

        this.videoSetupComplete = true;
      });
  }

  private determineVideoComponentType() {
    switch (this.videoStreamingProvider.VideoStreamingProviderId) {
      case VideoStreamingProviderAllItems.BunnyNet:
        this.resolveBunnyPlayerSelection();
        this.videoComponentType = this.useBunnyHlsPlayer
          ? VideoPlayerBunnyHlsComponent
          : VideoPlayerBunnyComponent;
        break;
      case VideoStreamingProviderAllItems.ApiVideo:
        this.videoComponentType = VideoPlayerApiVideoComponent;
        break;
    }
  }

  private applyPlayerTestSettings(settings: BunnyPlayerTestSettings): void {
    this.playerTestSettings = settings;
    this.playerTestSettingsActive = this.bunnyPlayerTestSettingsService.isConfigured;
  }

  private resolveBunnyPlayerSelection(): void {
    let selectedPlayback = this.playback;
    const mode = this.playerTestSettingsActive ? this.playerTestSettings?.mode : 'automatic';

    if (mode === 'bunny') {
      const source = this.playback?.LegacySource
        || (this.playback?.OnlineSource?.Format?.toLowerCase() === 'bunny-embed'
          ? this.playback.OnlineSource
          : null);
      if (source) {
        selectedPlayback = { ...this.playback, OnlineSource: source };
      }
    } else if (mode === 'vidstack') {
      const source = this.playback?.AuthenticatedSource
        || (this.playback?.OnlineSource?.Format?.toLowerCase() === 'hls'
          ? this.playback.OnlineSource
          : null);
      if (source) {
        selectedPlayback = { ...this.playback, OnlineSource: source };
      }
    }

    this.selectedPlayback = selectedPlayback;
    this.useBunnyHlsPlayer = selectedPlayback?.OnlineSource?.Format?.toLowerCase() === 'hls';
    this.showBunnyHlsDiagnostics = this.useBunnyHlsPlayer && this.playerTestSettings?.showDiagnostics === true;

    if (this.playerTestSettingsActive) {
      if (this.playerTestSettings.skipMode === 'allow') {
        this.effectiveAllowSeekOverride = true;
        this.preventSeekOverride = false;
      } else if (this.playerTestSettings.skipMode === 'prevent') {
        this.effectiveAllowSeekOverride = false;
        this.preventSeekOverride = true;
      } else if (this.playerTestSettings.skipMode === 'completed') {
        this.effectiveAllowSeekOverride = selectedPlayback?.LessonCompleted === true;
        this.preventSeekOverride = selectedPlayback?.LessonCompleted !== true;
      }
    }
  }

  configureVideoComponent(componentInstance: VideoComponentBase) {
    this.videoComponentInstance = componentInstance;

    this.videoComponentInstance.video = this.video;
    this.videoComponentInstance.videoLibrary = this.videoLibrary;
    this.videoComponentInstance.videoStreamingProvider = this.videoStreamingProvider;
    this.videoComponentInstance.furthestWatchedTime = this.furthestWatchedTime;

    this.videoComponentInstance.allowSeekOverride = this.effectiveAllowSeekOverride;
    this.videoComponentInstance.preventSeekOverride = this.preventSeekOverride;
    this.videoComponentInstance.playback = this.selectedPlayback;
    this.videoEventSubscription =
      this.videoComponentInstance.videoEvent.subscribe((videoEvent: VideoEvent) => {
        this.onVideoEvent(videoEvent);
        // if (videoEvent.videoDuration > 0) {
        //   videoEvent.percentComplete = videoEvent.furtherestVideoTime / videoEvent.videoDuration * 100;

        //   if (videoEvent.percentComplete > 99) {
        //     videoEvent.percentComplete = 100;
        //   }
        // }

        // this.videoBufferingUtilService.handleBufferingEvent(this.video, videoEvent);

        // this.videoEvent.emit(videoEvent);
      });

    this.initCompleteSubscription =
      this.videoComponentInstance.initCompleted.subscribe((initComplete: boolean) => {
        this.onInitCompleted(initComplete);
        // this.initComplete = initComplete;

        // this.initTime = performance.now() - this.initTimeStart;

        // console.log(`Video player init complete in ${this.initTime} milliseconds`);

        // if (this.hidePlayer || this.message) {
        //   this.hidePlayer = false;
        //   this.message = null;
        // }

        // this.initCompleted.emit(initComplete);
      });

    this.videoComponentInstance.setupVideo();

    if (!this.initTimeStart) {
      this.initTimeStart = performance.now();
    }

    this.videoComponentInstance.videoWidthChanged.subscribe(width => {
      this.onVideoWidthChanged(width);
      //this.videoWidthChanged.emit(width);
    });
  }

  onVideoWidthChanged(width: any) {
    this.videoWidthChanged.emit(width);
  }

  onVideoEvent(videoEvent: VideoEvent) {
    if (videoEvent.videoDuration > 0) {
      videoEvent.percentComplete = videoEvent.furtherestVideoTime / videoEvent.videoDuration * 100;

      if (videoEvent.percentComplete > 99) {
        videoEvent.percentComplete = 100;
      }
    }

    this.videoBufferingUtilService.handleBufferingEvent(this.video, videoEvent);

    this.videoEvent.emit(videoEvent);
  }

  onInitCompleted(initComplete: boolean) {
    this.initComplete = initComplete;

    this.initTime = performance.now() - this.initTimeStart;

    //console.log(`Video player init complete in ${this.initTime} milliseconds`);

    if (this.hidePlayer || this.message) {
      this.hidePlayer = false;
      this.message = null;
    }

    this.initCompleted.emit(initComplete);
  }
}
